import { Suspense } from 'react'
import { Sheet } from './Sheet'
import { ErrorBoundary } from './ErrorBoundary'
import { preloadable } from '../lib/preloadable'
import { useLocation } from '../lib/router'
import type { GoogleEvent } from '../lib/googleEvents'

const QuickAdd = preloadable(() => import('./sheets/QuickAdd'))
const DateSheet = preloadable(() => import('./sheets/DateSheet'))
const IconSheet = preloadable(() => import('./sheets/IconSheet'))
const FilterSheet = preloadable(() => import('./sheets/FilterSheet'))
const GoogleEventSheet = preloadable(() => import('./sheets/GoogleEventSheet'))
const AssignSheet = preloadable(() => import('./sheets/AssignSheet'))
const TagsSheet = preloadable(() => import('./sheets/TagsSheet'))
const GroupSheet = preloadable(() => import('./sheets/GroupSheet'))
const MoveSheet = preloadable(() => import('./sheets/MoveSheet'))
const ShareSheet = preloadable(() => import('./sheets/ShareSheet'))
const PullInSheet = preloadable(() => import('./sheets/PullInSheet'))
const DaysSheet = preloadable(() => import('./sheets/DaysSheet'))
const ActivitySheet = preloadable(() => import('./sheets/ActivitySheet'))
const AssistantSheet = preloadable(() => import('./sheets/AssistantSheet'))
const ConnectionsSheet = preloadable(() => import('./Connections'))
const ChatInstructionsSheet = preloadable(() => import('./sheets/ChatInstructionsSheet'))
const ViewSheet = preloadable(() => import('./sheets/ViewSheet'))
const ContextSheet = preloadable(() => import('./sheets/ContextSheet'))
const AttachmentSheet = preloadable(() => import('./Attachments'))
const CommandSheet = preloadable(() => import('./sheets/CommandSheet'))
const ChartSheet = preloadable(() => import('./sheets/ChartSheet'))
const ChartsSheet = preloadable(() => import('./sheets/ChartsSheet'))
const SourcesSheet = preloadable(() => import('./sheets/SourcesSheet'))
const DiagramFull = preloadable(() => import('./diagram/DiagramFull'))
const CalcSheet = preloadable(() => import('./sheets/CalcSheet'))
const LinkSheet = preloadable(() => import('./sheets/LinkSheet'))
const PhotoViewer = preloadable(() => import('./PhotoViewer'))

// Sheets are small; all of them are warmed up (several autofocus an input) — see usePreload in App.
const SHEETS = [
  CommandSheet,
  QuickAdd,
  DateSheet,
  AssignSheet,
  TagsSheet,
  MoveSheet,
  ShareSheet,
  GroupSheet,
  PullInSheet,
  DaysSheet,
  ViewSheet,
  ContextSheet,
  AttachmentSheet,
  ChartSheet,
  ChartsSheet,
  SourcesSheet,
  CalcSheet,
  LinkSheet,
  PhotoViewer,
]

export function preloadSheets() {
  for (const c of SHEETS) void c.preload().catch(() => {})
}

export function SheetHost() {
  const { sheet } = useLocation()
  if (!sheet) return null
  const data = (sheet.data ?? {}) as Record<string, unknown>
  let el: React.ReactNode = null
  switch (sheet.kind) {
    case 'add':
      el = <QuickAdd {...(data as object)} />
      break
    case 'row-menu':
      // A row's ⋯: the same item menu as everywhere, with Open.
      el = <ContextSheet id={String(data.id)} from="row" />
      break
    case 'date':
      el = <DateSheet id={data.id ? String(data.id) : undefined} mode={(data.mode as 'item' | 'later') ?? 'item'} />
      break
    case 'gevent':
      el = <GoogleEventSheet e={data.e as GoogleEvent} meId={String(data.meId ?? '')} />
      break
    case 'filter':
      el = <FilterSheet page={data.page === 'pages' ? 'pages' : 'notes'} />
      break
    case 'icon':
      el = <IconSheet id={String(data.id)} />
      break
    case 'repeat':
      el = <DateSheet id={String(data.id)} repeatOpen />
      break
    case 'assign':
      el = <AssignSheet id={String(data.id)} />
      break
    case 'tags':
      el = <TagsSheet id={String(data.id)} focus={data.focus ? String(data.focus) : undefined} />
      break
    case 'calc':
      el = <CalcSheet id={String(data.id)} field={String(data.field)} />
      break
    case 'move':
      el = <MoveSheet id={String(data.id)} />
      break
    case 'share':
      el = <ShareSheet id={String(data.id)} />
      break
    case 'chat-settings':
    case 'assistant':
      el = <AssistantSheet chat={data.chat === 'shared' ? 'shared' : data.chat === 'mine' ? 'mine' : undefined} />
      break
    case 'connections':
      el = <ConnectionsSheet />
      break
    case 'chat-instructions':
      el = <ChatInstructionsSheet chat={data.chat === 'shared' ? 'shared' : 'mine'} value={typeof data.value === 'string' ? data.value : ''} />
      break
    case 'activity-settings':
      el = <ActivitySheet />
      break
    case 'days':
      el = <DaysSheet />
      break
    case 'pull-in':
      el = <PullInSheet date={String(data.date)} />
      break
    case 'view':
      el = <ViewSheet id={data.id ? String(data.id) : undefined} />
      break
    case 'group':
      el = <GroupSheet view={String(data.view)} group={String(data.group ?? '')} label={String(data.label ?? '')} />
      break
    case 'context':
      el = <ContextSheet id={String(data.id)} view={data.view ? String(data.view) : undefined} page={!!data.page} />
      break
    case 'command':
      el = <CommandSheet />
      break
    case 'chart':
      el = <ChartSheet home={String(data.home)} id={String(data.id)} idea={data.idea ? String(data.idea) : undefined} />
      break
    case 'charts':
      el = <ChartsSheet home={String(data.home)} />
      break
    case 'sources':
      el = <SourcesSheet />
      break
    case 'attachment':
      el = <AttachmentSheet id={String(data.id)} attId={String(data.attId)} />
      break
    case 'link':
      el = <LinkSheet />
      break
    case 'photo':
      el = (
        <PhotoViewer
          id={data.id ? String(data.id) : undefined}
          attId={data.attId ? String(data.attId) : undefined}
          keys={Array.isArray(data.keys) ? data.keys.map(String) : undefined}
          i={Number(data.i ?? 0)}
          settings={!!data.settings}
        />
      )
      break
    case 'diagram':
      el = <DiagramFull item={String(data.item)} id={String(data.id)} />
      break
  }
  // A pop-up that fails to draw shows a closable note instead of blanking the whole app.
  return (
    <ErrorBoundary
      key={sheet.kind}
      label={`the ${sheet.kind} pop-up`}
      fallback={
        <Sheet title="Couldn’t open this">
          <p className="pb-3 text-sm text-muted">Something went wrong showing it. Close it and try again.</p>
        </Sheet>
      }
    >
      <Suspense fallback={null}>{el}</Suspense>
    </ErrorBoundary>
  )
}
